import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Card,
  Button,
  Typography,
  IconButton,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  CircularProgress,
  Skeleton,
  Tooltip,
  Alert,
  Snackbar,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { submitLaneDataAction, fetchLanes } from '../store/lane.actions';
import {
  selectSubmitStatus,
  selectSubmitError,
  selectSubmitResult,
  selectLanes,
  selectLanesStatus,
  selectDriveStartTime,
} from '../store/lane.selectors';
import { resetSubmitStatus } from '../store/lane.slice';
import { RequestStatus } from '../../../utils/enums';

// ============ Styled Components ============

const MainContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  padding: theme.spacing(2),
  height: 'calc(100vh - 160px)',
  backgroundColor: theme.palette.background.default,
  overflow: 'hidden',
  fontFamily: theme.typography.fontFamily,
  [theme.breakpoints.down('sm')]: {
    gap: theme.spacing(1),
    padding: theme.spacing(1),
    height: 'calc(100vh - 140px)',
    flexDirection: 'column',
  },
}));

const VideoSection = styled(Box)(({ theme }) => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  position: 'relative',
  minHeight: 0,
  [theme.breakpoints.down('sm')]: {
    minHeight: 'auto',
    gap: theme.spacing(1),
  },
}));

const VideoOverlay = styled(Box)(({ theme, position = 'left' }) => ({
  position: 'absolute',
  alignContent: 'center',
  [position]: 0,
  width: '140px',
  height: '32.5vh',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  padding: theme.spacing(1.5),
  borderRadius:
    position === 'left'
      ? `0 ${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0`
      : `${theme.shape.borderRadius}px 0 0 ${theme.shape.borderRadius}px`,
  overflowY: 'auto',
  zIndex: 10,
  '&::-webkit-scrollbar': {
    width: '4px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: theme.palette.primary.main,
    borderRadius: '2px',
  },
  [theme.breakpoints.down('xl')]: {
    width: '130px',
    height: '45vh',
    padding: theme.spacing(1.25),
    gap: theme.spacing(0.75),
  },
  [theme.breakpoints.down('lg')]: {
    width: '120px',
    padding: theme.spacing(1),
    gap: theme.spacing(0.5),
    height: '40vh',
  },
  [theme.breakpoints.down(1050)]: {
    width: '110px',
    padding: theme.spacing(0.9),
    gap: theme.spacing(0.4),
    height: '35vh',
  },
  [theme.breakpoints.down('md')]: {
    width: '100px',
    padding: theme.spacing(0.75),
    gap: theme.spacing(0.3),
    height: '30vh',
  },
  [theme.breakpoints.down(750)]: {
    width: '90px',
    padding: theme.spacing(0.6),
    gap: theme.spacing(0.2),
  },
  [theme.breakpoints.down('sm')]: {
    display: 'none',
  },
}));

const VideoPlayerCard = styled(Card)(({ theme }) => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
}));

const VideoContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  backgroundColor: '#000',
  overflow: 'hidden',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '300px',
  borderRadius: `${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0 0`,
  position: 'relative',
  '& video': {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
}));

const VideoInfoBar = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.5),
  borderTop: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  borderRadius: `0 0 ${theme.shape.borderRadius}px ${theme.shape.borderRadius}px`,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: theme.spacing(2),
  flexWrap: 'wrap',
  [theme.breakpoints.down('lg')]: {
    padding: theme.spacing(1),
    gap: theme.spacing(1),
  },
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(0.75),
    gap: theme.spacing(0.75),
    flexDirection: 'column',
    alignItems: 'stretch',
  },
}));

const SectorButtonsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
  flexShrink: 0,
  [theme.breakpoints.down('lg')]: {
    gap: theme.spacing(0.75),
  },
  [theme.breakpoints.down('md')]: {
    flexDirection: 'row',
    gap: theme.spacing(0.5),
    width: '100%',
  },
}));

const LineTypeButton = styled(Button)(
  ({ theme, selected = false, isRecording = false, overlay = false }) => ({
    padding: overlay ? theme.spacing(0.75) : theme.spacing(1.25),
    fontSize: overlay ? '0.8rem' : '0.7rem',
    textTransform: 'none',
    fontWeight: 600,
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    minHeight: overlay ? '50px' : '70px',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    border: `2px solid ${selected ? theme.palette.primary.main : overlay ? 'rgba(255,255,255,0.3)' : theme.palette.divider}`,
    backgroundColor: selected
      ? '#C8102E !important'
      : isRecording
        ? theme.palette.warning.light
        : overlay
          ? 'rgba(255,255,255,0.1)'
          : theme.palette.background.paper,
    color: selected ? '#fff' : overlay ? '#fff' : theme.palette.text.primary,
    boxShadow: selected ? theme.shadows[4] : 'none',
    '&:hover': {
      backgroundColor: selected
        ? 'rgba(102, 126, 234, 0.9)'
        : overlay
          ? 'rgba(255,255,255,0.2)'
          : theme.palette.action.hover,
      borderColor: theme.palette.primary.main,
      transform: overlay ? 'scale(1.05)' : 'translateY(-2px)',
      boxShadow: theme.shadows[6],
    },
    '&:active': {
      transform: overlay ? 'scale(0.98)' : 'translateY(0)',
    },
    [theme.breakpoints.down('xl')]: {
      padding: overlay ? theme.spacing(0.65) : theme.spacing(1.1),
      fontSize: overlay ? '0.62rem' : '0.68rem',
      minHeight: overlay ? '48px' : '68px',
    },
    [theme.breakpoints.down('lg')]: {
      padding: overlay ? theme.spacing(0.5) : theme.spacing(1),
      fontSize: overlay ? '0.6rem' : '0.65rem',
      minHeight: overlay ? '45px' : '60px',
    },
    [theme.breakpoints.down(1050)]: {
      padding: overlay ? theme.spacing(0.45) : theme.spacing(0.85),
      fontSize: overlay ? '0.57rem' : '0.62rem',
      minHeight: overlay ? '42px' : '55px',
    },
    [theme.breakpoints.down('md')]: {
      padding: overlay ? theme.spacing(0.4) : theme.spacing(0.75),
      fontSize: overlay ? '0.54rem' : '0.6rem',
      minHeight: overlay ? '40px' : '50px',
    },
    [theme.breakpoints.down(750)]: {
      padding: overlay ? theme.spacing(0.35) : theme.spacing(0.65),
      fontSize: overlay ? '0.5rem' : '0.55rem',
      minHeight: overlay ? '36px' : '45px',
    },
  })
);

const SectorButton = styled(Button)(({ theme, selected = false, isRecording = false }) => ({
  padding: theme.spacing(1.5),
  fontSize: '0.75rem',
  textTransform: 'none',
  fontWeight: 700,
  whiteSpace: 'normal',
  minHeight: '60px',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  border: `2px solid ${selected ? theme.palette.success.main : theme.palette.divider}`,
  backgroundColor: selected
    ? theme.palette.success.light
    : isRecording
      ? theme.palette.warning.light
      : theme.palette.background.paper,
  color: selected ? theme.palette.success.dark : theme.palette.text.primary,
  boxShadow: selected ? theme.shadows[4] : 'none',
  '&:hover': {
    backgroundColor: selected ? theme.palette.success.light : theme.palette.action.hover,
    borderColor: theme.palette.success.main,
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[6],
  },
  [theme.breakpoints.down('lg')]: {
    padding: theme.spacing(1),
    fontSize: '0.65rem',
    minHeight: '50px',
  },
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(0.75),
    fontSize: '0.6rem',
    minHeight: '45px',
  },
}));

const DataListCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(1.5),
  height: '100%',
  overflowY: 'auto',
  backgroundColor: theme.palette.background.paper,
  '&::-webkit-scrollbar': {
    width: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: theme.palette.divider,
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: theme.palette.primary.main,
    borderRadius: '4px',
    '&:hover': {
      background: theme.palette.primary.dark,
    },
  },
}));

const RightPanel = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1.5),
  minWidth: '320px',
  maxWidth: '420px',
  height: '100%',
  overflow: 'hidden',
  [theme.breakpoints.down('lg')]: {
    minWidth: '280px',
    maxWidth: '340px',
  },
  [theme.breakpoints.down('md')]: {
    minWidth: '250px',
    maxWidth: '300px',
  },
  [theme.breakpoints.down('sm')]: {
    minWidth: '100%',
    maxWidth: '100%',
    height: 'auto',
    maxHeight: '300px',
    borderTop: `1px solid ${theme.palette.divider}`,
  },
}));

const RecordingIndicator = styled(Box)(({ theme, isRecording = false }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(1.5),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: isRecording ? '#ffebee' : '#f5f5f5',
  border: `1px solid ${isRecording ? '#ffcdd2' : theme.palette.divider}`,
}));

// ============ Constants ============

const LINE_TYPES = [
  { id: 1, name: 'Dashed White', icon: '- - -', color: '#FFF', shortcut: '1' },
  { id: 2, name: 'Dashed Yellow', icon: '- - -', color: '#FFD700', shortcut: '2' },
  { id: 3, name: 'Double Yellow', icon: '= =', color: '#FFD700', shortcut: '3' },
  { id: 4, name: 'Solid White', icon: '___', color: '#FFF', shortcut: '4' },
  { id: 5, name: 'Solid Yellow', icon: '━━━', color: '#FFD700', shortcut: '5' },
  { id: 6, name: 'Single Yellow', icon: '−', color: '#FFD700', shortcut: '6' },
  { id: 7, name: 'Double White', icon: '∥', color: '#FFF', shortcut: '7' },
];

const SECTOR_TYPES = [
  { id: 'cycle', name: 'Cycle Lane', color: '#4CAF50', icon: '🚴' },
  { id: 'shoulder', name: 'Shoulder', color: '#757575', icon: '→' },
];

const formatTime = (seconds) => {
  if (!seconds || isNaN(seconds)) return '00:00';
  const minutes = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
};

const timeStringToSeconds = (timeStr) => {
  if (!timeStr) return 0;
  const [minutes, seconds] = timeStr.split(':').map(Number);
  return (minutes || 0) * 60 + (seconds || 0);
};

const validateTimeFormat = (timeStr) => {
  if (!timeStr) return false;
  const regex = /^\d{1,2}:\d{2}$/;
  if (!regex.test(timeStr)) return false;
  const [, seconds] = timeStr.split(':').map(Number);
  return seconds >= 0 && seconds < 60;
};

const getLineTypeId = (typeName) => {
  const lineType = LINE_TYPES.find((lt) => lt.name === typeName);
  return lineType ? lineType.id : 1;
};

/**
 * Builds the recorder's { left, right, aux } event lists from the backend's
 * response shape: an array of lanes, each with
 *   lines: { left: FormattedLine[], right: FormattedLine[], aux: FormattedLine[] }
 * where each FormattedLine has ISO `startTime`/`endTime` (absolute timestamps).
 *
 * driveStartTimeIso lets us convert those absolute timestamps back into the
 * video-relative seconds the recorder UI works in. If it's missing for some
 * reason, we fall back to using the earliest line start time in the data as
 * an approximate zero point rather than failing outright.
 */
const buildEventsFromLanes = (lanesArray, driveStartTimeIso) => {
  const left = [];
  const right = [];
  const aux = [];

  if (!Array.isArray(lanesArray) || lanesArray.length === 0) {
    return { left, right, aux };
  }

  lanesArray.forEach((lane) => {
    const lines = lane?.lines;
    if (!lines || typeof lines !== 'object') return;

    const allLines = [...(lines.left || []), ...(lines.right || []), ...(lines.aux || [])];
    if (allLines.length === 0) return;

    const referenceTimeMs = driveStartTimeIso
      ? new Date(driveStartTimeIso).getTime()
      : Math.min(...allLines.map((l) => new Date(l.startTime).getTime()));

    const toRelativeSeconds = (isoString) =>
      (new Date(isoString).getTime() - referenceTimeMs) / 1000;

    const mapLine = (line, idPrefix, extra = {}) => {
      const startTime = toRelativeSeconds(line.startTime);
      const endTime = toRelativeSeconds(line.endTime);
      return {
        id: `${idPrefix}_${lane.laneid}_${line.lineId}`,
        lineId: line.lineId,
        type: line.type,
        startTime,
        endTime,
        duration: endTime - startTime,
        status: 'finished',
        ...extra,
      };
    };

    (lines.left || []).forEach((line) => {
      left.push(
        mapLine(line, 'left', { lineTypeId: getLineTypeId(line.type), lanePosition: 'left' })
      );
    });

    (lines.right || []).forEach((line) => {
      right.push(
        mapLine(line, 'right', { lineTypeId: getLineTypeId(line.type), lanePosition: 'right' })
      );
    });

    (lines.aux || []).forEach((line) => {
      aux.push(
        mapLine(line, 'sector', {
          sectorTypeId: line.type === 'Cycle Lane' ? 'cycle' : 'shoulder',
          sectorType: 'sector',
        })
      );
    });
  });

  return { left, right, aux };
};

// Try to load selected drive info from localStorage (in case of page refresh)

const storedRow = localStorage.getItem('selectedDriveRow');
let row = null;
try {
  row = storedRow ? JSON.parse(storedRow) : null;
} catch (e) {
  row = null;
}

// ============ Main Component ============

const LaneMarkingRecorder = ({
  driveId = row?.id,
  driveName = row?.name || 'demo-drive',
  videoUrl = null || row?.video_url,
  currentTime = 0,
  userId = null,
  onEventsChange = null,
}) => {
  const navigate = useNavigate();
  // State for lane markings
  const [leftSectorEvents, setleftSectorEvents] = useState([]);
  const [rightSectorEvents, setrightSectorEvents] = useState([]);
  const [AuxillarysectorEvents, setAuxillarysectorEvents] = useState([]);
  const [initialLeftSectorEvents, setInitialLeftSectorEvents] = useState([]);
  const [initialRightSectorEvents, setInitialRightSectorEvents] = useState([]);
  const [initialAuxillarysectorEvents, setInitialAuxillarysectorEvents] = useState([]);
  const dispatch = useDispatch();
  // Recording state
  const [recordingLeftType, setRecordingLeftType] = useState(null);
  const [recordingLeftStartTime, setRecordingLeftStartTime] = useState(null);
  const [recordingRightType, setRecordingRightType] = useState(null);
  const [recordingRightStartTime, setRecordingRightStartTime] = useState(null);
  const [recordingSectorType, setRecordingSectorType] = useState(null);
  const [recordingSectorStartTime, setRecordingSectorStartTime] = useState(null);
  const [hasLoadedExistingLanes, setHasLoadedExistingLanes] = useState(false);

  const normalizeLaneEvents = (events) =>
    events.map(({ type, startTime, endTime }) => `${type}|${startTime}|${endTime}`).sort();

  const hasLaneEventChanges = useMemo(() => {
    const currentEvents = [
      ...normalizeLaneEvents(leftSectorEvents),
      ...normalizeLaneEvents(rightSectorEvents),
      ...normalizeLaneEvents(AuxillarysectorEvents),
    ];
    const baselineEvents = [
      ...normalizeLaneEvents(initialLeftSectorEvents),
      ...normalizeLaneEvents(initialRightSectorEvents),
      ...normalizeLaneEvents(initialAuxillarysectorEvents),
    ];

    if (!hasLoadedExistingLanes) {
      return currentEvents.length > 0;
    }

    if (currentEvents.length !== baselineEvents.length) return true;
    return !currentEvents.every((value, index) => value === baselineEvents[index]);
  }, [
    leftSectorEvents,
    rightSectorEvents,
    AuxillarysectorEvents,
    initialLeftSectorEvents,
    initialRightSectorEvents,
    initialAuxillarysectorEvents,
    hasLoadedExistingLanes,
  ]);

  // Editing state
  const [editingEventId, setEditingEventId] = useState(null);
  const [editStartTime, setEditStartTime] = useState('');
  const [editEventType, setEditEventType] = useState(null);
  const [localCurrentTime, setLocalCurrentTime] = useState(0);
  const [shortcutModalOpen, setShortcutModalOpen] = useState(false);

  // Selectors for submit status
  const submitStatus = useSelector(selectSubmitStatus);
  const submitError = useSelector(selectSubmitError);
  const submitResult = useSelector(selectSubmitResult);
  const lanes = useSelector(selectLanes);
  const lanesStatus = useSelector(selectLanesStatus);
  const driveStartTime = useSelector(selectDriveStartTime);
  const [showErrorAlert, setShowErrorAlert] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const isLoadingExistingLanes = lanesStatus === RequestStatus.LOADING && !hasLoadedExistingLanes;

  // Video ref for accessing current time
  const videoRef = useRef(null);

  // Handle submit status changes
  useEffect(() => {
    if (submitStatus === RequestStatus.SUCCEEDED) {
      setSnackbar({
        open: true,
        message: 'Events submitted successfully!',
        severity: 'success',
      });
      if (driveId) {
        setHasLoadedExistingLanes(false);
        dispatch(fetchLanes(driveId));
      }
      const timer = setTimeout(() => {
        dispatch(resetSubmitStatus());
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [submitStatus, dispatch, driveId]);

  // submitResult is already unwrapped by the action creator: { lanes, driveStartTime }
  useEffect(() => {
    if (submitStatus === RequestStatus.SUCCEEDED && submitResult?.lanes?.length > 0) {
      const { left, right, aux } = buildEventsFromLanes(
        submitResult.lanes,
        submitResult.driveStartTime
      );
      setleftSectorEvents(left);
      setrightSectorEvents(right);
      setAuxillarysectorEvents(aux);
      setInitialLeftSectorEvents(left);
      setInitialRightSectorEvents(right);
      setInitialAuxillarysectorEvents(aux);
      setHasLoadedExistingLanes(true);
    }
  }, [submitStatus, submitResult]);

  // Handle submit error changes
  useEffect(() => {
    if (submitStatus === RequestStatus.FAILED && submitError) {
      setShowErrorAlert(true);
      setSnackbar({
        open: true,
        message: submitError,
        severity: 'error',
      });
      const timer = setTimeout(() => {
        setShowErrorAlert(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [submitStatus, submitError]);

  // Reset submit status when component unmounts (when leaving page)
  useEffect(() => {
    return () => {
      dispatch(resetSubmitStatus());
    };
  }, [dispatch]);

  useEffect(() => {
    if (driveId) {
      dispatch(fetchLanes(driveId));
    }
  }, [dispatch, driveId]);

  useEffect(() => {
    setHasLoadedExistingLanes(false);
  }, [driveId]);

  useEffect(() => {
    if (lanesStatus !== RequestStatus.SUCCEEDED) return;

    if (!lanes || lanes.length === 0) {
      if (!hasLoadedExistingLanes) {
        setleftSectorEvents([]);
        setrightSectorEvents([]);
        setAuxillarysectorEvents([]);
        setHasLoadedExistingLanes(true);
      }
      return;
    }

    if (!hasLoadedExistingLanes) {
      const { left, right, aux } = buildEventsFromLanes(lanes, driveStartTime);
      setleftSectorEvents(left);
      setrightSectorEvents(right);
      setAuxillarysectorEvents(aux);
      setInitialLeftSectorEvents(left);
      setInitialRightSectorEvents(right);
      setInitialAuxillarysectorEvents(aux);
      setHasLoadedExistingLanes(true);
    }
  }, [lanes, lanesStatus, hasLoadedExistingLanes, driveStartTime]);

  // Get current video time
  const getCurrentVideoTime = () => {
    return videoRef.current?.currentTime || localCurrentTime || 0;
  };

  const handleRefreshVideo = () => {
    if (!videoRef.current) return;
    const currentTimeValue = videoRef.current.currentTime || localCurrentTime || 0;
    const wasPaused = videoRef.current.paused;
    const currentSrc = videoRef.current.src || row?.video_url || videoUrl;
    videoRef.current.src = currentSrc;
    videoRef.current.load();
    videoRef.current.currentTime = currentTimeValue;
    if (!wasPaused) {
      const playPromise = videoRef.current.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {});
      }
    }
  };

  // Handle left lane marking button click
  const handleLeftLineTypeClick = useCallback(
    (lineType) => {
      const videoTime = getCurrentVideoTime();
      if (recordingLeftType?.id === lineType.id) {
        // Stop recording
        const newEvent = {
          id: `left_${Date.now()}`,
          type: lineType.name,
          lineTypeId: lineType.id,
          lanePosition: 'left',
          startTime: recordingLeftStartTime,
          endTime: videoTime,
          duration: videoTime - recordingLeftStartTime,
          status: 'finished',
        };
        setleftSectorEvents([...leftSectorEvents, newEvent]);
        setRecordingLeftType(null);
        setRecordingLeftStartTime(null);
      } else {
        // If recording a different line type, stop the current recording first
        if (recordingLeftType) {
          const stopEvent = {
            id: `left_${Date.now()}`,
            type: recordingLeftType.name,
            lineTypeId: recordingLeftType.id,
            lanePosition: 'left',
            startTime: recordingLeftStartTime,
            endTime: videoTime,
            duration: videoTime - recordingLeftStartTime,
            status: 'finished',
          };
          setleftSectorEvents([...leftSectorEvents, stopEvent]);
        }
        // Start recording the new line type
        setRecordingLeftType(lineType);
        setRecordingLeftStartTime(videoTime);
      }
    },
    [recordingLeftType, recordingLeftStartTime, leftSectorEvents]
  );

  // Handle right lane marking button click
  const handleRightLineTypeClick = useCallback(
    (lineType) => {
      const videoTime = getCurrentVideoTime();
      if (recordingRightType?.id === lineType.id) {
        // Stop recording
        const newEvent = {
          id: `right_${Date.now()}`,
          type: lineType.name,
          lineTypeId: lineType.id,
          lanePosition: 'right',
          startTime: recordingRightStartTime,
          endTime: videoTime,
          duration: videoTime - recordingRightStartTime,
          status: 'finished',
        };
        setrightSectorEvents([...rightSectorEvents, newEvent]);
        setRecordingRightType(null);
        setRecordingRightStartTime(null);
      } else {
        // If recording a different line type, stop the current recording first
        if (recordingRightType) {
          const stopEvent = {
            id: `right_${Date.now()}`,
            type: recordingRightType.name,
            lineTypeId: recordingRightType.id,
            lanePosition: 'right',
            startTime: recordingRightStartTime,
            endTime: videoTime,
            duration: videoTime - recordingRightStartTime,
            status: 'finished',
          };
          setrightSectorEvents([...rightSectorEvents, stopEvent]);
        }
        // Start recording the new line type
        setRecordingRightType(lineType);
        setRecordingRightStartTime(videoTime);
      }
    },
    [recordingRightType, recordingRightStartTime, rightSectorEvents]
  );

  useEffect(() => {
    let pendingShortcutSide = null;
    let timeoutId = null;

    const handleShortcutKeyDown = (event) => {
      const key = event.key.toLowerCase();
      if (key === 'a') {
        pendingShortcutSide = 'left';
        clearTimeout(timeoutId);
        timeoutId = window.setTimeout(() => {
          pendingShortcutSide = null;
        }, 1200);
        return;
      }
      if (key === 'd') {
        pendingShortcutSide = 'right';
        clearTimeout(timeoutId);
        timeoutId = window.setTimeout(() => {
          pendingShortcutSide = null;
        }, 1200);
        return;
      }
      if (pendingShortcutSide && /^[1-5]$/.test(key)) {
        const lineType = LINE_TYPES.find((lt) => lt.shortcut === key);
        if (lineType) {
          if (pendingShortcutSide === 'left') {
            handleLeftLineTypeClick(lineType);
          } else {
            handleRightLineTypeClick(lineType);
          }
          event.preventDefault();
        }
        pendingShortcutSide = null;
        clearTimeout(timeoutId);
      }
    };

    window.addEventListener('keydown', handleShortcutKeyDown);
    return () => {
      window.removeEventListener('keydown', handleShortcutKeyDown);
      clearTimeout(timeoutId);
    };
  }, [handleLeftLineTypeClick, handleRightLineTypeClick]);

  // Handle sector type button click
  const handleSectorTypeClick = useCallback(
    (sectorType) => {
      const videoTime = getCurrentVideoTime();
      if (recordingSectorType?.id === sectorType.id) {
        // Stop recording
        const newEvent = {
          id: `sector_${Date.now()}`,
          type: sectorType.name,
          sectorTypeId: sectorType.id,
          sectorType: 'sector',
          startTime: recordingSectorStartTime,
          endTime: videoTime,
          duration: videoTime - recordingSectorStartTime,
          status: 'finished',
        };
        setAuxillarysectorEvents([...AuxillarysectorEvents, newEvent]);
        setRecordingSectorType(null);
        setRecordingSectorStartTime(null);
      } else {
        // If recording a different sector type, stop the current recording first
        if (recordingSectorType) {
          const stopEvent = {
            id: `sector_${Date.now()}`,
            type: recordingSectorType.name,
            sectorTypeId: recordingSectorType.id,
            sectorType: 'sector',
            startTime: recordingSectorStartTime,
            endTime: videoTime,
            duration: videoTime - recordingSectorStartTime,
            status: 'finished',
          };
          setAuxillarysectorEvents([...AuxillarysectorEvents, stopEvent]);
        }
        // Start recording the new sector type
        setRecordingSectorType(sectorType);
        setRecordingSectorStartTime(videoTime);
      }
    },
    [recordingSectorType, recordingSectorStartTime, currentTime, AuxillarysectorEvents]
  );

  // Edit event handlers
  const handleOpenEdit = (event, eventList) => {
    setEditingEventId(event.id);
    setEditStartTime(formatTime(event.startTime));
    setEditEventType({ event, list: eventList });
  };

  const handleSaveEdit = () => {
    if (!editEventType) return;

    // Validate time format
    if (!validateTimeFormat(editStartTime)) {
      setSnackbar({
        open: true,
        message: 'Invalid time format. Use MM:SS (e.g., 01:30)',
        severity: 'error',
      });
      return;
    }

    const startSeconds = timeStringToSeconds(editStartTime);
    const endSeconds = getCurrentVideoTime(); // Automatically fetch current video time

    // Validate time range
    if (startSeconds >= endSeconds) {
      setSnackbar({
        open: true,
        message: 'Start time must be before end time',
        severity: 'error',
      });
      return;
    }

    const updateEventInList = (list) =>
      list.map((event) =>
        event.id === editingEventId
          ? {
              ...event,
              startTime: startSeconds,
              endTime: endSeconds,
              duration: endSeconds - startSeconds,
            }
          : event
      );

    if (editEventType.list === 'left') {
      setleftSectorEvents(updateEventInList(leftSectorEvents));
    } else if (editEventType.list === 'right') {
      setrightSectorEvents(updateEventInList(rightSectorEvents));
    } else if (editEventType.list === 'sector') {
      setAuxillarysectorEvents(updateEventInList(AuxillarysectorEvents));
    }

    setEditingEventId(null);
    setEditEventType(null);
  };

  const handleCancelEdit = () => {
    setEditingEventId(null);
    setEditEventType(null);
  };

  // Delete event handler
  const handleDeleteEvent = (eventId, eventList) => {
    if (eventList === 'left') {
      setleftSectorEvents(leftSectorEvents.filter((event) => event.id !== eventId));
    } else if (eventList === 'right') {
      setrightSectorEvents(rightSectorEvents.filter((event) => event.id !== eventId));
    } else if (eventList === 'sector') {
      setAuxillarysectorEvents(AuxillarysectorEvents.filter((event) => event.id !== eventId));
    }
    // Show snackbar notification
    setSnackbar({
      open: true,
      message: 'Event deleted successfully! Click "Submit Events" to save changes.',
      severity: 'success',
    });
  };

  // Submit all events handler
  const handleSubmitEvents = () => {
    if (!hasLaneEventChanges) {
      alert('No new or modified events to submit!');
      return;
    }

    // Format events into lanes structure
    const lanesData = {};

    // Format left lane events
    if (leftSectorEvents.length > 0) {
      lanesData.left = {};
      leftSectorEvents.forEach((event, index) => {
        const lineKey = `line_${index + 1}`;
        lanesData.left[lineKey] = {
          type: event.type,
          start_time: event.startTime,
          end_time: event.endTime,
        };
      });
    }

    // Format right lane events
    if (rightSectorEvents.length > 0) {
      lanesData.right = {};
      rightSectorEvents.forEach((event, index) => {
        const lineKey = `line_${index + 1}`;
        lanesData.right[lineKey] = {
          type: event.type,
          start_time: event.startTime,
          end_time: event.endTime,
        };
      });
    }

    // Format sector (auxline) events
    if (AuxillarysectorEvents.length > 0) {
      lanesData.auxline = {};
      AuxillarysectorEvents.forEach((event, index) => {
        const lineKey = `line_${index + 1}`;
        lanesData.auxline[lineKey] = {
          type: event.type,
          start_time: event.startTime,
          end_time: event.endTime,
        };
      });
    }

    const submitData = {
      driveId: driveId,
      driveName,
      lanes: lanesData,
      userId,
    };
    if (onEventsChange) {
      onEventsChange(submitData);
    }
    dispatch(submitLaneDataAction(submitData));
  };

  // Combine all events for summary
  const allEvents = [...leftSectorEvents, ...rightSectorEvents, ...AuxillarysectorEvents];
  const hasLaneData = allEvents.length > 0;

  const EventListTable = ({ events, title, listType, isLoading = false }) => (
    <Box sx={{ mb: 3 }}>
      <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', mb: 1, color: 'primary.default' }}>
        {title} ({events.length})
      </Typography>
      {isLoading ? (
        <Box sx={{ display: 'grid', gap: 1 }}>
          {[...Array(4)].map((_, index) => (
            <Skeleton key={index} variant="rounded" height={44} animation="wave" />
          ))}
        </Box>
      ) : (
        <>
          {events.length === 0 ? (
            <Typography
              sx={{ textAlign: 'center', color: 'text.secondary', py: 2, fontSize: '0.75rem' }}
            >
              No {title.toLowerCase()} recorded
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem' }}>Type</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem' }}>Start</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem' }}>End</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem' }} align="center">
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {events.map((event) => (
                    <TableRow key={event.id} sx={{ '&:hover': { backgroundColor: '#f9f9f9' } }}>
                      <TableCell sx={{ fontSize: '0.7rem' }}>
                        <Typography sx={{ fontSize: '0.7rem', fontWeight: 600 }}>
                          {event.type}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.7rem' }}>
                        {editingEventId === event.id ? (
                          <TextField
                            size="small"
                            value={editStartTime}
                            onChange={(e) => setEditStartTime(e.target.value)}
                            placeholder="MM:SS"
                            helperText="MM:SS"
                            sx={{ width: '60px', '& input': { fontSize: '0.7rem', py: 0.5 } }}
                          />
                        ) : (
                          formatTime(event.startTime)
                        )}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.7rem' }}>
                        {editingEventId === event.id ? (
                          <Box sx={{ fontWeight: 600 }}>
                            {formatTime(getCurrentVideoTime())} (current)
                          </Box>
                        ) : (
                          formatTime(event.endTime)
                        )}
                      </TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.7rem' }}>
                        {editingEventId === event.id ? (
                          <>
                            <Tooltip title="Save">
                              <IconButton
                                size="small"
                                onClick={handleSaveEdit}
                                sx={{ color: 'success.main', p: 0.5 }}
                              >
                                <CheckIcon fontSize="small" sx={{ fontSize: '1rem' }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Cancel">
                              <IconButton
                                size="small"
                                onClick={handleCancelEdit}
                                sx={{ color: 'text.secondary', p: 0.5 }}
                              >
                                <CloseIcon fontSize="small" sx={{ fontSize: '1rem' }} />
                              </IconButton>
                            </Tooltip>
                          </>
                        ) : (
                          <>
                            <Tooltip title="Edit">
                              <IconButton
                                size="small"
                                onClick={() => handleOpenEdit(event, listType)}
                                sx={{ color: '#1976d2', p: 0.5 }}
                              >
                                <EditIcon fontSize="small" sx={{ fontSize: '1rem' }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteEvent(event.id, listType)}
                                sx={{ color: '#d32f2f', p: 0.5 }}
                              >
                                <DeleteIcon fontSize="small" sx={{ fontSize: '1rem' }} />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}
    </Box>
  );

  EventListTable.propTypes = {
    events: PropTypes.array.isRequired,
    title: PropTypes.string.isRequired,
    listType: PropTypes.string.isRequired,
    isLoading: PropTypes.bool,
  };

  return (
    <MainContainer>
      {/* CENTER - VIDEO PLAYER WITH OVERLAYS */}
      <VideoSection>
        <VideoPlayerCard>
          <VideoContainer>
            {/* LEFT LANE BUTTONS OVERLAY */}
            <VideoOverlay position="left">
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: '0.65rem',
                  color: '#C8102E',
                  backgroundColor: 'rgba(255,255,255,0.8)',
                  mb: 0.5,
                  textAlign: 'center',
                  position: 'sticky',
                  top: 0,
                  zIndex: 1,
                }}
              >
                LEFT LINE
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5,
                  flex: 1,
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  scrollbarColor: 'rgba(255,255,255,0.3) transparent',
                  direction: 'rtl',
                }}
              >
                {LINE_TYPES.map((lineType) => (
                  <Tooltip
                    key={lineType.id}
                    title={
                      lineType.shortcut
                        ? `${lineType.name} (A+${lineType.shortcut})`
                        : lineType.name
                    }
                  >
                    <LineTypeButton
                      overlay
                      selected={recordingLeftType?.id === lineType.id}
                      isRecording={recordingLeftType?.id === lineType.id}
                      onClick={() => handleLeftLineTypeClick(lineType)}
                      variant="outlined"
                      size="large"
                      sx={{
                        backdropFilter: 'blur(8px)',
                        background: 'rgba(0,0,0,0.3)',
                      }}
                    >
                      {lineType.name}
                    </LineTypeButton>
                  </Tooltip>
                ))}
              </Box>
            </VideoOverlay>

            {/* RIGHT LANE BUTTONS OVERLAY */}
            <VideoOverlay position="right">
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: '0.65rem',
                  color: '#C8102E',
                  backgroundColor: 'rgba(255,255,255,0.8)',
                  mb: 0.5,
                  textAlign: 'center',
                  position: 'sticky',
                  top: 0,
                  zIndex: 1,
                  flexShrink: 0,
                }}
              >
                RIGHT LINE
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5,
                  flex: 1,
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  scrollbarColor: 'rgba(255,255,255,0.3) transparent',
                }}
              >
                {LINE_TYPES.map((lineType) => (
                  <Tooltip
                    key={lineType.id}
                    title={
                      lineType.shortcut
                        ? `${lineType.name} (D+${lineType.shortcut})`
                        : lineType.name
                    }
                  >
                    <LineTypeButton
                      overlay
                      selected={recordingRightType?.id === lineType.id}
                      isRecording={recordingRightType?.id === lineType.id}
                      onClick={() => handleRightLineTypeClick(lineType)}
                      variant="outlined"
                      size="small"
                      sx={{
                        backdropFilter: 'blur(8px)',
                        background: 'rgba(0,0,0,0.3)',
                      }}
                    >
                      {lineType.name}
                    </LineTypeButton>
                  </Tooltip>
                ))}
              </Box>
            </VideoOverlay>

            {videoUrl ? (
              <Box
                ref={videoRef}
                component="video"
                src={videoUrl}
                controls
                controlsList="nodownload"
                style={{ width: '100%', height: '100%' }}
                onTimeUpdate={(e) => setLocalCurrentTime(e.currentTarget.currentTime)}
              />
            ) : (
              <Typography sx={{ color: '#999', textAlign: 'center', fontSize: '0.9rem' }}>
                🎬 No Video Available
                <br />
                <span
                  style={{ fontSize: '0.75rem', color: '#666', marginTop: '8px', display: 'block' }}
                >
                  Drive Name: {driveName}
                </span>
              </Typography>
            )}
          </VideoContainer>

          <VideoInfoBar>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Box>
                  <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                    Current Time
                  </Typography>
                  <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, fontFamily: 'monospace' }}>
                    {formatTime(localCurrentTime || currentTime)}
                  </Typography>
                </Box>
                <Tooltip title="Refresh video">
                  <IconButton
                    size="small"
                    onClick={handleRefreshVideo}
                    sx={{ color: 'text.secondary', p: 0.5 }}
                  >
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Shortcut keys: A + 1..5 for left, D + 1..5 for right">
                  <IconButton
                    size="small"
                    onClick={() => setShortcutModalOpen(true)}
                    sx={{ color: 'text.secondary', p: 0.5 }}
                  >
                    <InfoOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>

              {recordingLeftType && (
                <RecordingIndicator isRecording>
                  <CircularProgress size={16} />
                  <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#d32f2f' }}>
                    Recording Left: {recordingLeftType.name}
                  </Typography>
                </RecordingIndicator>
              )}

              {recordingRightType && (
                <RecordingIndicator isRecording>
                  <CircularProgress size={16} />
                  <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#d32f2f' }}>
                    Recording Right: {recordingRightType.name}
                  </Typography>
                </RecordingIndicator>
              )}

              {recordingSectorType && (
                <RecordingIndicator isRecording>
                  <CircularProgress size={16} />
                  <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#d32f2f' }}>
                    Recording Sector: {recordingSectorType.name}
                  </Typography>
                </RecordingIndicator>
              )}
            </Box>

            {/* Sector Buttons Inside Video Info Bar */}
            <SectorButtonsContainer>
              {SECTOR_TYPES.map((sectorType) => (
                <Tooltip key={sectorType.id} title={sectorType.name}>
                  <SectorButton
                    selected={recordingSectorType?.id === sectorType.id}
                    isRecording={recordingSectorType?.id === sectorType.id}
                    onClick={() => handleSectorTypeClick(sectorType)}
                    variant="outlined"
                    sx={{ minWidth: 'auto', flex: 1, minHeight: '40px', py: 0.75 }}
                  >
                    {sectorType.icon}
                  </SectorButton>
                </Tooltip>
              ))}
            </SectorButtonsContainer>
          </VideoInfoBar>
        </VideoPlayerCard>
        <Modal
          open={shortcutModalOpen}
          onClose={() => setShortcutModalOpen(false)}
          aria-labelledby="shortcut-info-title"
          aria-describedby="shortcut-info-description"
        >
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 320,
              bgcolor: 'background.paper',
              borderRadius: 2,
              boxShadow: 24,
              p: 3,
              outline: 'none',
            }}
          >
            <Typography
              id="shortcut-info-title"
              variant="h6"
              component="h2"
              sx={{ mb: 1, fontWeight: 700 }}
            >
              Shortcut Key Help
            </Typography>
            <Typography
              id="shortcut-info-description"
              sx={{ mb: 2, fontSize: '0.9rem', color: 'text.secondary' }}
            >
              Use the keyboard shortcuts to start or stop left/right lane recording quickly.
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
              <Typography sx={{ fontSize: '0.9rem' }}>
                • Press <strong>A</strong> + <strong>1</strong> for Dashed White (left)
              </Typography>
              <Typography sx={{ fontSize: '0.9rem' }}>
                • Press <strong>A</strong> + <strong>2</strong> for Dashed Yellow (left)
              </Typography>
              <Typography sx={{ fontSize: '0.9rem' }}>
                • Press <strong>A</strong> + <strong>3</strong> for Double Yellow (left)
              </Typography>
              <Typography sx={{ fontSize: '0.9rem' }}>
                • Press <strong>A</strong> + <strong>4</strong> for Solid White (left)
              </Typography>
              <Typography sx={{ fontSize: '0.9rem' }}>
                • Press <strong>A</strong> + <strong>5</strong> for Solid Yellow (left)
              </Typography>
              <Typography sx={{ fontSize: '0.9rem' }}>
                • Press <strong>D</strong> + <strong>1</strong> for Dashed White (right)
              </Typography>
              <Typography sx={{ fontSize: '0.9rem' }}>
                • Press <strong>D</strong> + <strong>2</strong> for Dashed Yellow (right)
              </Typography>
              <Typography sx={{ fontSize: '0.9rem' }}>
                • Press <strong>D</strong> + <strong>3</strong> for Double Yellow (right)
              </Typography>
              <Typography sx={{ fontSize: '0.9rem' }}>
                • Press <strong>D</strong> + <strong>4</strong> for Solid White (right)
              </Typography>
              <Typography sx={{ fontSize: '0.9rem' }}>
                • Press <strong>D</strong> + <strong>5</strong> for Solid Yellow (right)
              </Typography>
            </Box>
            <Button variant="contained" fullWidth onClick={() => setShortcutModalOpen(false)}>
              Close
            </Button>
          </Box>
        </Modal>
      </VideoSection>

      {/* RIGHT PANEL - EVENTS LIST WITH SUBMIT BUTTON */}
      <RightPanel>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <Typography
            sx={{ fontWeight: 700, fontSize: '0.85rem', mb: 1, color: 'text.primary', px: 1 }}
          >
            ALL EVENTS ({allEvents.length})
          </Typography>
          <DataListCard>
            <EventListTable
              events={leftSectorEvents}
              title="Left Line"
              listType="left"
              isLoading={isLoadingExistingLanes}
            />
            <EventListTable
              events={rightSectorEvents}
              title="Right Line"
              listType="right"
              isLoading={isLoadingExistingLanes}
            />
            <EventListTable
              events={AuxillarysectorEvents}
              title="Auxiliary Line"
              listType="sector"
              isLoading={isLoadingExistingLanes}
            />
            {allEvents.length === 0 && (
              <Typography
                sx={{
                  textAlign: 'center',
                  color: 'text.secondary',
                  py: 3,
                  fontSize: '0.75rem',
                  fontStyle: 'italic',
                }}
              >
                Click buttons to start recording lane markings
              </Typography>
            )}
          </DataListCard>
        </Box>
        {showErrorAlert && submitError && (
          <Alert severity="error" sx={{ mb: 1.5, fontSize: '0.75rem' }}>
            {submitError}
          </Alert>
        )}
        <Button
          onClick={handleSubmitEvents}
          variant="contained"
          fullWidth
          disabled={!hasLaneEventChanges || submitStatus === RequestStatus.LOADING}
          sx={{
            mt: 1.5,
            py: 1.5,
            fontSize: '0.85rem',
            fontWeight: 700,
            textTransform: 'none',
            boxShadow: 2,
            background:
              hasLaneEventChanges && submitStatus !== RequestStatus.LOADING
                ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                : 'grey.400',
            color: '#fff',
            cursor: submitStatus !== RequestStatus.LOADING ? 'pointer' : 'not-allowed',
            '&:hover': {
              boxShadow: submitStatus !== RequestStatus.LOADING ? 4 : 2,
              opacity: submitStatus !== RequestStatus.LOADING ? 0.9 : 1,
            },
            '&:disabled': {
              cursor: 'not-allowed',
              opacity: 0.6,
            },
          }}
        >
          {submitStatus === RequestStatus.LOADING ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={18} sx={{ color: 'inherit' }} />
              <span>Submitting...</span>
            </Box>
          ) : submitStatus === RequestStatus.SUCCEEDED ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckIcon sx={{ fontSize: '1.2rem' }} />
              <span>Submitted</span>
            </Box>
          ) : (
            'Submit Events'
          )}
        </Button>
        <Button
          onClick={() => navigate(`/lane-map/${driveId}`)}
          variant="outlined"
          fullWidth
          disabled={!hasLaneData}
          sx={{
            mt: 1.5,
            py: 1.5,
            fontSize: '0.85rem',
            fontWeight: 700,
            textTransform: 'none',
            boxShadow: 2,
            background: hasLaneData
              ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
              : 'grey.400',
            color: '#fff',
            cursor: hasLaneData ? 'pointer' : 'not-allowed',
            '&:hover': {
              boxShadow: hasLaneData ? 4 : 2,
              opacity: hasLaneData ? 0.9 : 1,
            },
            '&:disabled': {
              cursor: 'not-allowed',
              opacity: 0.6,
            },
          }}
        >
          View Lane Map
        </Button>
      </RightPanel>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={1000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        message={snackbar.message}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </MainContainer>
  );
};

LaneMarkingRecorder.propTypes = {
  driveId: PropTypes.string,
  driveName: PropTypes.string,
  videoUrl: PropTypes.string,
  currentTime: PropTypes.number,
  userId: PropTypes.string,
  onEventsChange: PropTypes.func,
};

LaneMarkingRecorder.defaultProps = {
  driveId: null,
  driveName: 'demo-drive',
  videoUrl: null,
  currentTime: 0,
  userId: null,
  onEventsChange: null,
};

export default LaneMarkingRecorder;
